<script setup lang="ts">
import {
  type ColumnType,
  type LinkToAnotherRecordType,
  PermissionEntity,
  PermissionKey,
  type TableType,
  UITypes,
  isLinksOrLTAR,
  isVirtualCol,
} from 'nocodb-sdk'
import { fieldMatchesSearch, isBlankFieldValue } from './searchUtils'

// Cells whose editor paints a format hint (e.g. "YYYY-MM-DD") as its own
// placeholder when empty — skip '--' overlay for these so the two don't
// stack. Their *readonly* variant has no such hint (see
// cell/Date/Readonly.vue), so '--' is still shown when the cell is rendered
// read-only — see showCompactEmptyHint below.
const COMPACT_FORMAT_HINT_UIDTS = new Set<string>([UITypes.Date, UITypes.DateTime, UITypes.Time, UITypes.Year])

// Cells that always paint their own visible empty-state UI in both editable
// and readonly modes — '--' would stack on top, so always skip:
//   Attachment → upload prompt / file list
//   Rating     → zero filled stars
//   Checkbox   → unchecked indicator
//   Button     → button label / icon
const COMPACT_ALWAYS_SKIP_UIDTS = new Set<string>([
  UITypes.Attachment,
  UITypes.Rating,
  UITypes.Checkbox,
  UITypes.Button,
])

const props = defineProps<{
  fields: ColumnType[]
  forceVerticalMode?: boolean
  isLoading: boolean
  showColCallback?: (col: ColumnType) => boolean
  isHiddenCol?: boolean
  /** Free-text query. fieldMatchesSearch normalizes internally so callers can pass raw input. */
  searchQuery?: string
  hideBlankFields?: boolean
  /** When true, render label + plain text instead of label + bordered input */
  compactMode?: boolean
}>()

const { changedColumns, localOnlyChanges, isNew, loadRow: _loadRow, row: _row } = useExpandedFormStoreOrThrow()

const { isSqlView } = useSmartsheetStoreOrThrow()

const isPublic = inject(IsPublicInj, ref(false))

const meta = inject(MetaInj, ref())

const isTemplateMode = inject(IsTemplateModeInj, ref(false))

const blueprintParentTableId = inject(BlueprintParentTableIdInj, ref())

const parentBreadcrumbs = inject(TemplateBreadcrumbsInj, ref([]))

const { isUIAllowed } = useRoles()

const { isMobileMode } = useGlobal()

const { getMeta } = useMetas()

const { open: openExpandedFormDetached } = useExpandedFormDetached()

const readOnly = computed(() => !isUIAllowed('dataEdit') || isPublic.value || isSqlView.value)

// In compact view, show '--' for an empty cell unless the cell already
// renders its own empty-state UI. Format-hint cells (date/time) only paint
// their hint in the editable variant — keep '--' when they mount read-only.
const showCompactEmptyHint = (col: ColumnType, isAllowed: boolean) => {
  if (!col.uidt) return true
  if (COMPACT_ALWAYS_SKIP_UIDTS.has(col.uidt)) return false
  if (!COMPACT_FORMAT_HINT_UIDTS.has(col.uidt)) return true
  return readOnly.value || !isAllowed || isSyncedColumn(col) || showReadonlyColumnTooltip(col)
}

/**
 * Check if an LTAR column points back to the parent table that opened this blueprint form.
 * Such columns are auto-linked and should be disabled (read-only) in the blueprint editor
 * to prevent circular linking.
 */
const isParentLtarColumn = (col: ColumnType): boolean => {
  if (!blueprintParentTableId.value || !isLinksOrLTAR(col)) return false
  const colOptions = col.colOptions as LinkToAnotherRecordType
  return colOptions?.fk_related_model_id === blueprintParentTableId.value
}

/**
 * Open a nested blueprint form for the given LTAR column.
 * When saved, the blueprint is added to the parent row's ltarState,
 * which will later be resolved into a real record when the template is used.
 */
const addBlueprintForColumn = async (col: ColumnType) => {
  const colOptions = col.colOptions as LinkToAnotherRecordType
  const relatedTableId = colOptions?.fk_related_model_id
  const baseId = meta.value?.base_id
  if (!relatedTableId || !baseId) return

  try {
    const relatedMeta = (await getMeta(baseId, relatedTableId)) as TableType
    if (!relatedMeta) return

    // Build breadcrumb trail: append current table name to parent trail
    const breadcrumbs = [...parentBreadcrumbs.value, meta.value?.title || '']

    openExpandedFormDetached({
      isOpen: true,
      row: { row: {}, oldRow: {}, rowMeta: { new: true } },
      meta: relatedMeta,
      loadRow: false,
      useMetaFields: true,
      blueprintMode: true,
      blueprintParentTableId: meta.value?.id,
      breadcrumbs,
      newRecordSubmitBtnText: 'Save Record',
      newRecordHeader: `New ${relatedMeta.title} Record`,
      createdRecord: (record: Record<string, any>) => {
        const blueprint = { ...record, _isBlueprint: true }
        // Ensure ltarState structure exists
        if (!_row.value.rowMeta) _row.value.rowMeta = {}
        if (!_row.value.rowMeta.ltarState) _row.value.rowMeta.ltarState = {}

        if (isHm(col) || isMm(col)) {
          if (!_row.value.rowMeta.ltarState[col.title!]) {
            _row.value.rowMeta.ltarState[col.title!] = []
          }
          _row.value.rowMeta.ltarState[col.title!].push(blueprint)
        } else {
          // BT or OO — single linked record
          _row.value.rowMeta.ltarState[col.title!] = blueprint
        }
      },
    })
  } catch (e) {
    console.error('Failed to open blueprint form:', e)
  }
}

const getRelatedTableName = (col: ColumnType): string => {
  const colOptions = col.colOptions as LinkToAnotherRecordType
  const relatedTableId = colOptions?.fk_related_model_id
  const baseId = meta.value?.base_id
  if (!relatedTableId || !baseId) return 'Sub Record'
  const { getMetaByKey } = useMetas()
  const relatedMeta = getMetaByKey(baseId, relatedTableId)
  return relatedMeta?.title || 'Sub Record'
}

const showCol = (col: ColumnType) => {
  const baseVisible = props.showColCallback?.(col) || !isVirtualCol(col) || !isNew.value || isLinksOrLTAR(col)
  if (!baseVisible) return false
  if (!fieldMatchesSearch(col, props.searchQuery ?? '', _row.value?.row)) return false
  if (props.hideBlankFields && col.title && isBlankFieldValue(_row.value?.row?.[col.title])) return false
  return true
}

const revertLocalOnlyChanges = (col: string) => {
  if (localOnlyChanges.value[col]) {
    _row.value.row[col] = localOnlyChanges.value[col]
    changedColumns.value.delete(col)
    delete localOnlyChanges.value[col]
  }
}

const isSyncedColumn = (column: ColumnType) => meta.value?.synced && column?.readonly && !isAutoGeneratedColumn(column)
</script>

<template>
  <div
    v-for="col of fields"
    v-show="showCol(col)"
    :key="col.title"
    :class="[`nc-expand-col-${col.title}`, { 'nc-row-compact': compactMode }]"
    :col-id="col.id"
    :data-testid="`nc-expand-col-${col.title}`"
    class="nc-expanded-form-row w-full"
  >
    <div
      class="flex items-start nc-expanded-cell min-h-[32px]"
      :class="{
        'flex-row <lg:flex-col <lg:w-full': !props.forceVerticalMode,
        'flex-col w-full': props.forceVerticalMode,
      }"
    >
      <div
        class="flex-none flex items-center rounded-lg overflow-hidden"
        :class="{
          'w-45 <lg:w-full <lg:px-0 <lg:mb-2 h-[32px] xs:h-auto sm:mx-2': !props.forceVerticalMode,
          'w-full px-0 mb-2 h-auto': props.forceVerticalMode,
        }"
      >
        <LazySmartsheetHeaderVirtualCell
          v-if="isVirtualCol(col)"
          :column="col"
          class="nc-expanded-cell-header h-full flex-none"
          :is-hidden-col="isHiddenCol"
          show-lock-icon
          show-menu-mobile
        />
        <LazySmartsheetHeaderCell
          v-else
          :column="col"
          class="nc-expanded-cell-header flex-none"
          :is-hidden-col="isHiddenCol"
          show-lock-icon
          show-menu-mobile
        />
      </div>

      <a-skeleton-input
        v-if="isLoading"
        active
        class="flex-none h-8 <lg:!w-full lg:flex-1 !rounded-lg !overflow-hidden"
        :class="{
          '!h-[151px]': isTextArea(col),
          '!h-[118px]': isAttachment(col),
          '!h-[80px]': isQrCode(col),
          '!h-[64px]': isBarcode(col),
          '!h-[38px]': isButton(col),
        }"
        size="small"
      />
      <NcTooltip
        v-else
        :tooltip-style="{ zIndex: '1049' }"
        class="!<lg:w-full !<lg:flex-none lg:flex-1 flex"
        :class="{
          'w-full !flex-none': props.forceVerticalMode,
          'lg:max-w-[calc(100%_-_188px)]': !props.forceVerticalMode,
        }"
        :placement="isMobileMode ? 'top' : 'right'"
        :disabled="!showReadonlyColumnTooltip(col) && !isParentLtarColumn(col)"
        :arrow="false"
      >
        <template #title>{{
          isParentLtarColumn(col) ? 'This field will be auto-linked to the parent record' : $t('msg.info.fieldReadonly')
        }}</template>
        <PermissionsTooltip
          v-if="col.title"
          class="w-full"
          :tooltip-style="{ zIndex: '1049' }"
          :entity="PermissionEntity.FIELD"
          :entity-id="col.id"
          :permission="PermissionKey.RECORD_FIELD_EDIT"
          :placement="isMobileMode ? 'top' : 'right'"
          :show-pointer-event-none="false"
          hide-on-click
          :disabled="showReadonlyColumnTooltip(col) || !showEditRestrictedColumnTooltip(col)"
        >
          <template #default="{ isAllowed }">
            <SmartsheetDivDataCell
              class="flex-1 flex relative"
              :class="[
                compactMode
                  ? 'min-h-4 items-start !bg-transparent pl-1 pr-1 -mt-0.5'
                  : 'min-h-8 items-center bg-nc-bg-default px-1',
                {
                  'w-full': props.forceVerticalMode,
                  '!select-text nc-system-field !bg-nc-bg-gray-extralight !text-nc-content-inverted-primary-disabled':
                    showReadonlyColumnTooltip(col) || isParentLtarColumn(col),
                  '!select-text nc-readonly-div-data-cell': readOnly || !isAllowed || isSyncedColumn(col),
                  'nc-data-cell-compact': compactMode,
                },
              ]"
            >
              <span
                v-if="compactMode && col.title && isBlankFieldValue(_row.row[col.title]) && showCompactEmptyHint(col, isAllowed)"
                class="nc-compact-empty-placeholder absolute left-1 inset-y-0 z-10 flex items-center text-nc-content-gray-muted text-[13px] pointer-events-none select-none"
              >
                --
              </span>
              <LazySmartsheetVirtualCell
                v-if="isVirtualCol(col)"
                v-model="_row.row[col.title]"
                :column="col"
                :read-only="readOnly || !isAllowed || isSyncedColumn(col) || isParentLtarColumn(col)"
                :row="_row"
                :is-allowed="isAllowed"
              />

              <LazySmartsheetCell
                v-else
                v-model="_row.row[col.title]"
                :active="true"
                :column="col"
                :edit-enabled="true"
                :read-only="
                  ncIsPlaywright()
                    ? readOnly || !isAllowed || isSyncedColumn(col)
                    : readOnly || !isAllowed || showReadonlyColumnTooltip(col) || isSyncedColumn(col)
                "
                :is-allowed="isAllowed"
                @update:model-value="changedColumns.add(col.title)"
              />
            </SmartsheetDivDataCell>
          </template>
        </PermissionsTooltip>
      </NcTooltip>
      <div
        v-if="col.title && localOnlyChanges[col.title]"
        class="flex items-center justify-center cursor-pointer relative"
        @click="revertLocalOnlyChanges(col.title)"
      >
        <GeneralIcon
          class="absolute right-1 top-2 text-nc-content-gray-muted hover:text-nc-content-gray-subtle my-auto"
          icon="reload"
        />
      </div>
    </div>

    <!-- Add Blueprint button below LTAR fields in template mode -->
    <div
      v-if="isTemplateMode && isLinksOrLTAR(col) && !readOnly && !isParentLtarColumn(col)"
      class="flex items-center"
      :class="{
        'flex-row <lg:pl-0': !props.forceVerticalMode,
        'pl-0': props.forceVerticalMode,
      }"
    >
      <div v-if="!props.forceVerticalMode" class="flex-none w-45 <lg:hidden sm:mx-2" />
      <div class="flex flex-col gap-1.5 mt-3">
        <span class="text-[11px] text-nc-content-gray-muted">Or, create and link a new record</span>
        <div class="flex items-center gap-2">
          <NcButton type="secondary" size="small" @click.stop="addBlueprintForColumn(col)">
            <div class="flex items-center gap-1">
              <GeneralIcon icon="plus" class="h-3.5 w-3.5" />
              <span>New {{ getRelatedTableName(col) }} Record</span>
            </div>
          </NcButton>
          <NcTooltip placement="bottom" class="flex items-center">
            <template #title>
              A new {{ getRelatedTableName(col) }} record will be created and linked each time this template is used
            </template>
            <GeneralIcon icon="info" class="h-3.5 w-3.5 text-nc-content-gray-subtle" />
          </NcTooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.ant-select-selector) {
  @apply !xs:h-full;
}

.nc-data-cell {
  @apply !rounded-lg;
  transition: all 0.3s;

  &:not(:focus-within):not(.nc-data-cell-compact):hover:not(.nc-readonly-div-data-cell):not(.nc-system-field):not(
      .nc-virtual-cell-button
    ) {
    @apply !border-1;

    &:not(.nc-attachment-cell):not(.nc-virtual-cell-button) {
      box-shadow: 0px 0px 4px 0px rgba(var(--rgb-base), 0.12);
    }
  }

  .nc-cell,
  .nc-virtual-cell {
    @apply h-auto;
  }

  &.nc-readonly-div-data-cell,
  &.nc-system-field {
    @apply !border-nc-border-gray-medium;

    .nc-cell,
    .nc-virtual-cell {
      @apply text-nc-content-gray-muted;
    }
  }

  &.nc-readonly-div-data-cell:focus-within,
  &.nc-system-field:focus-within {
    @apply !border-nc-border-gray-medium;
  }

  &:focus-within:not(.nc-readonly-div-data-cell):not(.nc-system-field):not(.nc-data-cell-compact) {
    @apply !shadow-selected;
  }

  :deep(.nc-lookup-cell) {
    .nc-qrcode-container {
      height: 100%;
    }

    .nc-multi-select {
      > div {
        margin-top: 3px;
      }
    }
  }

  &:has(.nc-virtual-cell-qrcode .nc-qrcode-container),
  &:has(.nc-virtual-cell-barcode .nc-barcode-container) {
    @apply !border-none px-0 !rounded-none;

    :deep(.nc-virtual-cell-qrcode),
    :deep(.nc-virtual-cell-barcode) {
      @apply px-0;

      & > div {
        @apply !px-0;
      }

      .barcode-wrapper {
        @apply ml-0;
      }
    }

    :deep(.nc-virtual-cell-qrcode) {
      img {
        @apply !h-full border-1 border-solid border-nc-border-gray-medium rounded;
      }
    }

    :deep(.nc-virtual-cell-barcode) {
      .nc-barcode-container {
        @apply border-1 rounded-lg border-nc-border-gray-medium h-[64px] max-w-full p-2 dark:bg-white;

        svg {
          @apply !h-full;
        }
      }
    }
  }

  .nc-cell-json {
    @apply;
  }
}

.nc-mentioned-cell {
  box-shadow: 0px 0px 0px 2px var(--ant-primary-color-outline) !important;
  @apply !border-nc-border-brand !border-1;
}

.nc-data-cell:focus-within:not(.nc-data-cell-compact) {
  @apply !border-1 !border-nc-border-brand;
}

:deep(.nc-system-field input) {
  @apply bg-transparent;
}

:deep(.nc-data-cell .nc-cell .nc-cell-field) {
  @apply px-2;
}

:deep(.nc-data-cell .nc-virtual-cell .nc-cell-field) {
  @apply px-2;
}

:deep(.nc-data-cell .nc-cell-field.nc-lookup-cell .nc-cell-field) {
  @apply px-0;
}

/* Compact view — strip vertical padding from the inner widget so rows pack
   tighter. Horizontal padding (px-2) is preserved so values still have left
   breathing room. Targets the textarea/input/select widgets that have their
   own hardcoded !py-1 (e.g. cell/Text/index.vue). */
:deep(.nc-data-cell-compact textarea),
:deep(.nc-data-cell-compact input),
:deep(.nc-data-cell-compact .ant-select-selector),
:deep(.nc-data-cell-compact .nc-cell-field) {
  @apply !py-0 !pl-0;
}

/* Compact view — make the inner widgets' background transparent so the
   absolute-positioned empty-state '--' placeholder behind them remains
   visible. */
:deep(.nc-data-cell-compact textarea),
:deep(.nc-data-cell-compact input) {
  background: transparent !important;
}

/* Compact view — flatten the field label too: remove its own pt-0.5 (added by
   SmartsheetHeaderCell in expanded-form mode) and the label-container's mb-2
   so the label sits flush with the value below. */
.nc-row-compact .nc-expanded-cell-header {
  @apply !py-0;
}
.nc-row-compact .nc-expanded-cell > :first-child {
  @apply !mb-0;
}

/* Compact view — drop the field-type icon (T / calendar / link / etc.) from
   the label line so the label text starts at the wrapper edge. */
.nc-row-compact :deep(.nc-cell-icon),
.nc-row-compact :deep(.nc-virtual-cell-icon) {
  display: none !important;
}

/* Compact view — hide the empty-cell '--' placeholder once the user clicks
   into the cell to edit. */
.nc-data-cell-compact:focus-within .nc-compact-empty-placeholder {
  display: none !important;
}
</style>
